import type { CSSProperties, ReactNode } from 'react';

import type { BadgeProps } from '@interstate/components/Badge';
import { Badge } from '@interstate/components/Badge';
import { CheckmarkSolidIcon, ExclamationTriangleIcon } from '@interstate/components/Icons';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';

import { CALCULATED_BADGE, DEAL_TYPE_SOURCE_BADGE_TEST_ID, LENDER_PROGRAM_SOURCED_BADGE } from './constants';
import { DealTypeSource, useDealTypeSource } from './useDealTypeSource';

const badgeSlotStyle: CSSProperties = { display: 'inline-flex', alignItems: 'center', flexShrink: 0 };

export type BadgeSpec = { label: string; variant: BadgeProps['variant']; startIcon?: ReactNode; sx?: BadgeProps['sx'] };

const DealTypeSourceBadge = () => {
    const theme = useInterstateTheme();
    const source = useDealTypeSource();

    if (!source) {
        return null;
    }

    const badge: BadgeSpec =
        source === DealTypeSource.PROGRAM
            ? {
                  label: LENDER_PROGRAM_SOURCED_BADGE,
                  variant: 'success',
                  startIcon: <CheckmarkSolidIcon aria-hidden={true} />
              }
            : {
                  label: CALCULATED_BADGE,
                  variant: 'caution',
                  // The design's amber is the data-viz ramp, not `caution`'s own token.
                  sx: { backgroundColor: theme.tokens.BaseColorAmber200 },
                  startIcon: <ExclamationTriangleIcon color={theme.tokens.BaseColorAmber500} aria-hidden={true} />
              };

    return (
        <span style={badgeSlotStyle}>
            <Badge variant={badge.variant} data-testid={DEAL_TYPE_SOURCE_BADGE_TEST_ID} sx={badge.sx} startIcon={badge.startIcon}>
                {badge.label}
            </Badge>
        </span>
    );
};

export default DealTypeSourceBadge;
